// @flow
import * as React from 'react';
import type { CurrentUser } from 'api/currentUser';
import type { PublicPage } from 'api/website';
import { withRouter } from 'react-router';
import type { RouterHistory, Location } from 'react-router';
import { connect } from 'react-redux';
import { isDefined } from 'utils/functional';
import { hasParam, getParam } from 'utils/url';
import { isStaff as userIsStaff } from 'utils/currentUser';
import { detectSearch } from 'utils/hero-searches';
import { scrollToHash } from 'utils/scrollIntoView';
import { hasBackground } from 'components/Cms/getContentBlock';
import AmpContext from 'utils/AmpContext';
import { PrimaryButtonLink } from 'components/Button';
import SearchToolbar from 'components/SearchToolbar';
import Box from 'components/Box';
import Icon from 'components/Icon';
import MainSearchContainer from 'containers/MainSearchContainer';
import PostSearchContainer from 'containers/PostSearchContainer';
import SearchPageContainer from 'containers/SearchPageContainer';
import ContentPage from 'pages/ContentPage';
import AmpContentPage from 'pages/AmpContentPage';
import { setDocumentTitle } from 'utils/meta';
import { showModal as showModalRaw } from '../redux/main/actions/modal';
import { pushFlashMessage as pushFlashMessageRaw } from '../redux/main/actions/flashMessages';
import { updateCurrentUser as updateCurrentUserRaw } from '../redux/main/actions/currentUser';

const { useState, useEffect, useContext, useCallback } = React;

const ContentPageContainer = ({
  content,
  showModal,
  pushFlashMessage,
  updateCurrentUser,
  hasGradProgram,
  isStaff,
  location,
  history,
  loggedIn,
  currentUser,
  gMapsErrored
}: {|
  content: PublicPage,
  showModal: Function,
  pushFlashMessage: Function,
  updateCurrentUser: Function,
  hasGradProgram: boolean,
  isStaff: boolean,
  location: Location,
  history: RouterHistory,
  loggedIn: boolean,
  currentUser: ?CurrentUser,
  publishPage: Function,
  gMapsErrored: boolean
|}) => {
  const subsite =
    location.state && location.state.subsite
      ? location.state.subsite
      : content.subsite;
  const parentSubsite =
    location.state && location.state.parentSubsite
      ? location.state.parentSubsite
      : null;
  const hasHomePage = !!content.subsite.homePage;
  const subsiteHasHomePage = hasHomePage || !!subsite.homePage;
  const heroSearch = content.heroSearch;
  const defaultSearchType = heroSearch
    ? heroSearch.defaultSearchFilters.type
    : subsite.defaultSearchType;
  const onlySearchResults = heroSearch && heroSearch.onlyResults;
  const onlyArticleResults = content.onlySearchResults && hasHomePage;
  const searchParam = detectSearch(location.search);
  const isPostSearch = onlyArticleResults || hasParam(location.search, 'pq');
  const postSearchDisabled = isPostSearch && !hasHomePage;
  const [exposeSiteNav, setExposeSiteNav] = useState(false);
  const requestIsAmp = useContext(AmpContext);

  const getSearchMode = useCallback(
    () =>
      onlySearchResults ||
      (onlyArticleResults && hasHomePage) ||
      (searchParam && !postSearchDisabled),
    [
      hasHomePage,
      onlyArticleResults,
      onlySearchResults,
      postSearchDisabled,
      searchParam
    ]
  );

  const [searchMode, setSearchMode] = useState(getSearchMode());

  const onClickContent = e => {
    const el = e.target;
    const href = el.getAttribute('href');
    if (el.nodeName === 'A' && /^#/.test(href)) {
      e.preventDefault();
      scrollToHash(href);
    }
  };

  useEffect(
    () => {
      setSearchMode(getSearchMode());
    },
    [onlySearchResults, onlyArticleResults, searchParam, getSearchMode]
  );

  useEffect(
    () => {
      setDocumentTitle(content.headTitle || content.title);
    },
    [content.headTitle, content.title]
  );

  useEffect(
    () => {
      if (hasParam(location.search, 'block')) {
        const el = document.getElementById(
          `cms-block-${getParam(location, 'block')}`
        );
        if (el) {
          window.scrollBy(0, el.getBoundingClientRect().top - 200);
        }
      }
    },
    [location]
  );

  useEffect(() => {
    if (window.location.hash) {
      window.setTimeout(() => {
        scrollToHash(window.location.hash.split('?')[0], {
          padding: 32,
          noTransition: true
        });
      }, 1);
    }
  }, []);

  useEffect(
    () => {
      const mapIsFullScreen = content.contentBlocks.some(
        block =>
          block.type === 'IDEALIST_DAYS_MAP' && block.data.defaultFullscreen
      );
      setExposeSiteNav(mapIsFullScreen);
    },
    [content.contentBlocks]
  );

  // CMS pages might not have all the available translations
  const url =
    content.translations &&
    content.translations.reduce(
      (acc, cur) => {
        acc[cur.locale] = `/${cur.url}`;
        return acc;
      },
      { [content.locale]: content.url }
    );

  const SearchResultsContainer = isPostSearch
    ? PostSearchContainer
    : MainSearchContainer;

  const homePageUrl = subsite.homePage ? subsite.homePage.url : '';
  const lastBlock =
    content.contentBlocks && content.contentBlocks.length
      ? content.contentBlocks[content.contentBlocks.length - 1]
      : null;
  const fullWidthLayout =
    (content.layout === 'FULL_WIDTH' || content.layout === 'FULL_WIDTH_PLUS') &&
    !content.sidebar;
  const hideFooterDivider =
    isDefined(lastBlock) &&
    ((lastBlock.type === 'SPLIT_CONTENT' &&
      lastBlock.data.appearance === 'FULL_WIDTH' &&
      fullWidthLayout) ||
      [
        'GRAD_CTA_HERO',
        'CTA_HERO',
        'STICKY_HEADER_LINKS_GRAD',
        'STICKY_HEADER_LINKS',
        'IDEALIST_DAYS_MAP'
      ].includes(lastBlock.type) ||
      hasBackground({ block: lastBlock, fullWidthLayout }));

  // Search bar at the top of page only should appear on "content pages"
  const hasPostSearchToolbar =
    subsiteHasHomePage && content.hasSearch && !searchMode;
  const ampHasPostSearchToolbar =
    (subsiteHasHomePage && content.hasSearch) || (searchMode && isPostSearch);

  const contentPage = requestIsAmp ? (
    <AmpContentPage
      content={content}
      loggedIn={loggedIn}
      hasGradProgram={hasGradProgram}
      pageUrl={url[content.locale]}
      hasPostSearchToolbar={ampHasPostSearchToolbar}
      searchMode={searchMode}
      onlySearchResults={onlySearchResults}
    />
  ) : (
    <div onClick={onClickContent}>
      <ContentPage
        isStaff={isStaff}
        content={content}
        subsite={subsite}
        withBanner={isDefined(heroSearch)}
        loggedIn={loggedIn}
        currentUser={currentUser}
        hasGradProgram={hasGradProgram}
        showModal={showModal}
        pushFlashMessage={pushFlashMessage}
        updateCurrentUser={updateCurrentUser}
        pageUrl={url[content.locale]}
        locale={content.locale}
        homePageUrl={homePageUrl}
        history={history}
        showListingCounts={!!heroSearch && heroSearch.showCounts}
        fullWidthLayout={fullWidthLayout}
        gMapsErrored={gMapsErrored}
        setExposeSiteNav={setExposeSiteNav}
      />
    </div>
  );

  const staffTools = isStaff ? (
    <Box
      p="0 !important"
      component={PrimaryButtonLink}
      href={`/cms/subsite/${
        content.subsite.id
      }/${content.type.toLowerCase()}s/${content.id}/edit`}
      data-qa-id="edit-button"
      aria-label={getText('Edit')}
      boxShadow="0 2px 3px rgba(0,0,0,0.25)"
    >
      <Box
        width={40}
        height={40}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Icon name="edit" width={24} fill="currentColor" />
      </Box>
    </Box>
  ) : null;

  return (
    <SearchPageContainer
      isPostSearch={isPostSearch}
      postSearchToolbar={
        hasPostSearchToolbar ? (
          <SearchToolbar
            content={content}
            subsite={subsite}
            searchMode={searchMode}
          />
        ) : (
          undefined
        )
      }
      hero={
        (searchMode && isPostSearch) ||
        (heroSearch && heroSearch.onlyResults && !requestIsAmp)
          ? undefined
          : heroSearch
      }
      staffTools={staffTools}
      onlySearchResults={onlySearchResults || onlyArticleResults}
      results={
        <SearchResultsContainer
          content={content}
          defaultSearchType={defaultSearchType}
          isStaff={isStaff}
          parentSubsite={parentSubsite}
        />
      }
      content={contentPage}
      layout={content.layout}
      hideFooterDivider={hideFooterDivider}
      subsiteId={subsite && subsite.id}
      url={url}
      searchMode={searchMode}
      setSearchMode={setSearchMode}
      defaultSearchType={defaultSearchType}
      navSection="MAIN"
      darkMode={searchMode}
      showListingCounts={heroSearch && heroSearch.showCounts}
      exposeSiteNav={exposeSiteNav}
    />
  );
};

const mapStateToProps = ({ currentUser, gMapsError: { errored } }) => {
  const loggedIn = currentUser && currentUser.email !== null;
  const hasGradProgram =
    currentUser &&
    currentUser.orgs &&
    currentUser.orgs.some(org => org.hasGradProgram);
  const gMapsErrored = errored;

  return {
    hasGradProgram,
    isStaff: userIsStaff(currentUser),
    loggedIn,
    currentUser,
    gMapsErrored
  };
};

export default withRouter(
  connect(
    mapStateToProps,
    {
      showModal: showModalRaw,
      pushFlashMessage: pushFlashMessageRaw,
      updateCurrentUser: updateCurrentUserRaw
    }
  )(ContentPageContainer)
);
